import { FulfillmentMethod } from '../types/shopConfig/shopConfigV2';
import { PayoutOptions } from './constants';
import { PayoutType } from '../types/apollo/generated/types.generated';
import { ListingWithAuthor } from '../types/sharetribe/listing';
import { GenericFilterOption } from '../types/filters/filters';

export function getLabel(options: GenericFilterOption[], key: string) {
  const matchingOption = options.find((option) => option.key === key);
  return matchingOption ? matchingOption.label : key;
}

// Get short label if its defined, but default to regular label if not
export function getShortLabel(options: GenericFilterOption[], key: string) {
  const matchingOption = options.find((option) => option.key === key);
  const shortLabel = matchingOption?.shortLabel;
  return shortLabel || getLabel(options, key);
}

export const buildDomainForAuthCookie = () => {
  // Example values for origin are 'http://www.boyish.treet.co'
  // or 'http://boyish.lvh.me:3000'.
  const url = new URL(window.location.origin.replace('www.', ''));

  let shouldRemoveSubdomain;
  if (
    url.hostname.includes('treet.co') ||
    url.hostname.includes('treet-test.co') ||
    url.hostname.includes('treet-staging.co') ||
    url.hostname.includes('lvh.me') // for local dev purposes
  ) {
    shouldRemoveSubdomain = true;
  } else {
    // Otherwise this is a custom url without a subdomain
    // e.g. marketplace.goodfair.com
    shouldRemoveSubdomain = false;
  }

  let cookieDomain;
  if (shouldRemoveSubdomain) {
    const domain = url.hostname
      .split('.')
      .slice(1) // strip out subdomain
      .join('.'); // e.g. 'treet.co'

    // Preprend a '.' to the cookie domain so this cookie can be passed to
    // all treet subdomains. This is necessary because the google auth callback
    // url will not contain the correct subdomain, meaning that the default
    // cookie passing params will not work.
    cookieDomain = `.${domain}`;
  } else {
    cookieDomain = url.hostname;
  }

  return cookieDomain;
};

// taken from https://dev.to/sobiodarlington/better-error-handling-with-async-await-2e5m
/**
 * @description ### Returns Go / Lua like responses(data, err)
 * when used with await
 *
 * - Example response [ data, undefined ]
 * - Example response [ undefined, Error ]
 *
 *
 * When used with Promise.all([req1, req2, req3])
 * - Example response [ [data1, data2, data3], undefined ]
 * - Example response [ undefined, Error ]
 *
 *
 * When used with Promise.race([req1, req2, req3])
 * - Example response [ data, undefined ]
 * - Example response [ undefined, Error ]
 *
 * @param {Promise} promise
 * @returns {Promise} [ data, undefined ]
 * @returns {Promise} [ undefined, Error ]
 */
export const handle = (promise: any) =>
  promise
    .then((data: any) => [data, undefined])
    .catch((error: any) => Promise.resolve([undefined, error]));

export const getFulfillmentMethod = (
  listings: ListingWithAuthor[],
  defaultBrandFulfillmentMethod: any
): FulfillmentMethod => {
  const validFullfillmentMethods = Object.values(FulfillmentMethod);

  const authorFullfillmentMethod = listings
    .map((listing) => listing.author?.attributes?.profile?.publicData?.fulfillmentMethod)
    .find((fulfillmentMethod) => fulfillmentMethod !== null && fulfillmentMethod !== undefined);

  if (authorFullfillmentMethod && validFullfillmentMethods.includes(authorFullfillmentMethod)) {
    return authorFullfillmentMethod;
  }

  const areAllListingsBrandDirect = listings.every(
    (listing) => listing.attributes?.publicData?.isBrandDirect === true
  );

  return areAllListingsBrandDirect ? defaultBrandFulfillmentMethod : FulfillmentMethod.Treet;
};

export const getPayoutTypeFromFormPayoutOption = (formPayoutOption: PayoutOptions) => {
  switch (formPayoutOption) {
    case PayoutOptions.Cash:
      return PayoutType.Cash;

    case PayoutOptions.Credit:
      return PayoutType.Credit;

    default:
      return undefined;
  }
};

export default { getLabel };
